import React, { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ATTESTATIONS = [
  "I am of legal age (21+) and legally eligible in my jurisdiction to purchase and handle research materials.",
  "I am purchasing for legitimate research purposes only. These materials are Research Use Only — not for human consumption, veterinary use, personal use, or the diagnosis, treatment, cure, or prevention of any disease.",
  "I understand Molecularix Biolabs does not provide dosing or administration instructions, and I will not rely on this site for any such information.",
  "I have read and agree to the Terms & Conditions, Privacy Policy, and Research-Use-Only Policy.",
];

export default function Gate({ onComplete }) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const emailValid = /^[^@]+@[^@]+\.[^@]+$/.test(email.trim());
  const ready = agreed && (email.trim() === "" || emailValid);

  const denied = () => { window.location.href = "https://www.google.com"; };

  const submit = async () => {
    if (!agreed) { toast.error("Please tick the attestation box to continue"); return; }
    if (email.trim() && !emailValid) { toast.error("Enter a valid email address, or leave it blank"); return; }
    setLoading(true);
    try {
      let vid = localStorage.getItem("mx_visitor");
      if (!vid) {
        vid = (window.crypto && window.crypto.randomUUID) ? window.crypto.randomUUID() : `v-${Date.now()}-${Math.random().toString(36).slice(2)}`;
        localStorage.setItem("mx_visitor", vid);
      }
      localStorage.setItem("mx_attested", new Date().toISOString());
      localStorage.setItem("mx_terms_accepted", new Date().toISOString());
      axios.post(`${API}/attestations`, { visitor_id: vid, statements: ATTESTATIONS }).catch((err) => console.warn("attestation log failed", err));

      if (email.trim() && emailValid) {
        const clean = email.trim().toLowerCase();
        try {
          const { data } = await axios.post(`${API}/subscribe`, { email: clean, source: "age_gate", consent: true });
          localStorage.setItem("mx_email", clean);
          const referred = !!localStorage.getItem("mx_ref_applied");
          if (data.welcome_code && !referred) {
            localStorage.setItem("mx_promo", JSON.stringify({ code: data.welcome_code, percent: data.discount_percent }));
            toast.success(`Welcome. Code ${data.welcome_code} unlocks ${data.discount_percent}% off your first order.`, { duration: 9000 });
          } else {
            toast.success("Welcome to the research catalog.");
          }
        } catch (e) { /* optional signup must never block catalog entry */ }
      }
      onComplete();
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/85 backdrop-blur-2xl px-6" data-testid="gate-overlay">
      <div className="absolute inset-0 noise-overlay opacity-[0.05]" />
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className="relative w-full max-w-2xl border border-white/10 bg-[#0C0C0C]/90 p-8 md:p-12 max-h-[92vh] overflow-y-auto" data-testid="age-gate"
        role="dialog" aria-modal="true" aria-label="Research eligibility attestation">
        <img src="/brand/molecularix-logo.webp" alt="Molecularix Biolabs — owl and DNA emblem"
          className="w-56 md:w-72 h-auto mx-auto mb-5 mix-blend-screen" data-testid="gate-brand-logo" />
        <div className="flex items-center justify-center mb-6">
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#D4AF37]">Research Eligibility Attestation</p>
        </div>
        <h1 className="font-serif text-3xl md:text-4xl font-light leading-tight mb-3">Confirm Your Eligibility</h1>
        <p className="text-[#A1A1AA] text-sm leading-relaxed mb-7">
          Molecularix Biolabs supplies reference-grade research compounds exclusively for legitimate laboratory and in-vitro research.
        </p>

        <div className="border border-[#1E1E24] bg-[#0A0A0C] p-5 md:p-6 mb-5" data-testid="gate-attestations-card">
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#D4AF37]/80 mb-4">Required Attestations</p>
          <ul className="space-y-3">
            <li className="flex items-start gap-3 text-sm leading-relaxed text-[#A1A1AA]"><span className="text-[#D4AF37] mt-0.5 shrink-0">•</span><span>I am of legal age <span className="text-[#F4F4F5]">(21+)</span> and legally eligible in my jurisdiction to purchase and handle research materials.</span></li>
            <li className="flex items-start gap-3 text-sm leading-relaxed text-[#A1A1AA]"><span className="text-[#D4AF37] mt-0.5 shrink-0">•</span><span>I am purchasing for legitimate research purposes only. These materials are <span className="text-[#F4F4F5]">Research Use Only</span> — not for human consumption, veterinary use, personal use, or the diagnosis, treatment, cure, or prevention of any disease.</span></li>
            <li className="flex items-start gap-3 text-sm leading-relaxed text-[#A1A1AA]"><span className="text-[#D4AF37] mt-0.5 shrink-0">•</span><span>I understand Molecularix Biolabs does <span className="text-[#F4F4F5]">not provide dosing or administration instructions</span>, and I will not rely on this site for any such information.</span></li>
            <li className="flex items-start gap-3 text-sm leading-relaxed text-[#A1A1AA]"><span className="text-[#D4AF37] mt-0.5 shrink-0">•</span><span>I have read and agree to the{" "}
              <a href="/policy/terms" target="_blank" rel="noreferrer" className="text-[#D4AF37] underline underline-offset-2" data-testid="gate-terms-link">Terms &amp; Conditions</a>,{" "}
              <a href="/policy/privacy" target="_blank" rel="noreferrer" className="text-[#D4AF37] underline underline-offset-2" data-testid="gate-privacy-link">Privacy Policy</a>, and{" "}
              <a href="/policy/research-use" target="_blank" rel="noreferrer" className="text-[#D4AF37] underline underline-offset-2" data-testid="gate-ruo-link">Research-Use-Only Policy</a>.</span></li>
          </ul>
        </div>

        <button type="button" onClick={() => setAgreed((c) => !c)} data-testid="gate-consent-checkbox"
          aria-pressed={agreed}
          className={`w-full min-h-[48px] flex items-center gap-4 text-left group border p-3.5 transition-colors ${agreed ? "border-[#D4AF37]/60 bg-[#D4AF37]/[0.06]" : "border-[#1E1E24] bg-[#0A0A0C] hover:border-[#D4AF37]/40"}`}>
          <span className={`shrink-0 w-7 h-7 flex items-center justify-center border transition-colors ${agreed ? "bg-[#D4AF37] border-[#D4AF37]" : "border-white/25 group-hover:border-[#D4AF37]/60"}`}>
            {agreed && <Check size={17} className="text-black" strokeWidth={3} />}
          </span>
          <span className={`text-[13px] md:text-sm leading-relaxed transition-colors ${agreed ? "text-[#F4F4F5]" : "text-[#A1A1AA]"}`}>
            I am 21+ years of age, purchasing strictly for Research Use Only (RUO), and agree to all legal attestations, Terms, and Policies above.
          </span>
        </button>

        <label htmlFor="gate-email" className="block font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A] mt-6 mb-2">Email <span className="text-[#D4AF37]/70">(optional)</span> — get a welcome discount</label>
        <input id="gate-email" value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="your@email.com (optional)"
          data-testid="email-input"
          onKeyDown={(e) => e.key === "Enter" && submit()}
          className="w-full bg-transparent border-b border-zinc-600 focus:border-[#D4AF37] outline-none py-3 text-lg font-serif placeholder:text-[#52525B] transition-colors" />

        <button onClick={submit} disabled={loading || !ready} data-testid="email-submit-btn"
          className="group mt-7 w-full min-h-[48px] inline-flex items-center justify-center gap-2 bg-[#D4AF37] px-8 py-4 text-black text-xs font-mono uppercase tracking-[0.2em] hover:bg-[#C5A028] transition-colors disabled:opacity-30 disabled:cursor-not-allowed">
          {loading ? "Entering…" : "Agree & Proceed"} <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
        </button>
        <button onClick={denied} data-testid="age-deny-btn"
          className="mt-3 w-full min-h-[48px] px-8 py-3 border border-white/15 text-[#A1A1AA] text-xs font-mono uppercase tracking-[0.2em] hover:border-white/40 hover:text-white transition-colors">
          Exit
        </button>
      </motion.div>
    </div>
  );
}
